import { useEffect, useMemo, useState } from "react"
import { Head } from "@inertiajs/react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { CsrfField, TextField } from "@/components/tuw/auth/fields"
import CountryRegionFields, {
  LABEL_CLASS,
  PICKER_CLASS,
  REGIONS,
  withCurrent,
} from "@/components/tuw/onboarding/CountryRegionFields"
import OnboardingLayout, { StepTitle, stepKicker } from "@/components/tuw/onboarding/OnboardingLayout"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { Label } from "@/components/ui/label"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

export { REGIONS }

// Onboarding's own words for the shared country and region pickers.
const PICKER_COPY = {
  countryLabel: "Country",
  countryPlaceholder: "Select a country",
  countrySearch: "Search countries…",
  countryEmpty: "No country found.",
  regionLabel: "Region",
  regionPlaceholder: "Select",
  regionSearch: "Search regions…",
  regionEmpty: "No region found.",
  regionOptionalLabel: (
    <>
      Region / state <Optional />
    </>
  ),
  regionTextPlaceholder: "Region",
}

const USERNAME_HINT = "Freelancers see your name unless you set a username and switch it on in Settings."

export function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

// The browser's clock offset, in minutes, for an IANA zone at an instant.
function offsetAt(tz, date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric",
  }).formatToParts(date)
  const get = (type) => Number(parts.find((part) => part.type === type).value)
  return (Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute")) - date.getTime()) / 60000
}

// Two zones keep the same clock when their offsets agree in winter and in
// summer: America/Toronto is not on Rails' list, but it runs on New York time.
function sameClock(a, b) {
  const year = new Date().getUTCFullYear()
  try {
    return [Date.UTC(year, 0, 1), Date.UTC(year, 6, 1)].every((t) => offsetAt(a, new Date(t)) === offsetAt(b, new Date(t)))
  } catch {
    return false
  }
}

// The zone to preselect: a saved zone the customer can no longer change, else
// the browser's zone (by name, then by clock), else a saved zone the list carries.
export function initialTimezone(current, isLocked, timezones) {
  if (isLocked) return current
  const detected = detectTimezone()
  const match =
    detected && (timezones.find((zone) => zone.tzid === detected) || timezones.find((zone) => sameClock(zone.tzid, detected)))
  if (match) return match.value
  return timezones.some((zone) => zone.value === current) ? current : ""
}

function Optional() {
  return (
    <span className="rounded-[20px] bg-secondary px-[7px] py-px text-[11px] font-semibold text-ink-3">Optional</span>
  )
}

// Step three of onboarding (P11-H6 Address Book): the legacy fields, posted
// as `customer[...]` and `user[timezone]` with `_method=put` to the same
// update action, whose rules are unchanged (already-set fields render
// read-only and are ignored on the server). The country and time zone pickers
// carry their value in a hidden field; a country with regions requires one.
export default function Profile({
  email,
  rail,
  initials,
  signOut,
  action,
  usernameCheck,
  values,
  locked,
  timezones,
  flash = {},
}) {
  const [country, setCountry] = useState(values.country)
  const [region, setRegion] = useState(values.region)
  const [timezone, setTimezone] = useState(() => initialTimezone(values.timezone, locked.timezone, timezones))
  const [username, setUsername] = useState(values.username)
  const [availability, setAvailability] = useState(null)
  const [regionMissing, setRegionMissing] = useState(false)
  const regions = REGIONS[country] ?? null

  // The legacy availability check, debounced: POST { username } → { available, message }.
  useEffect(() => {
    if (locked.username || username.length < 3) {
      setAvailability(null)
      return undefined
    }
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(usernameCheck, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
          body: JSON.stringify({ username }),
        })
        const data = await response.json()
        setAvailability({ ok: data.available === true, text: (data.message ?? "").replace(/^\((.*)\)$/, "$1") })
      } catch {
        setAvailability(null)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [username, usernameCheck, locked.username])

  const timezoneOptions = useMemo(() => withCurrent(timezones, timezone), [timezones, timezone])

  function handleSubmit(event) {
    if (!regions || region || locked.region) return
    event.preventDefault()
    setRegionMissing(true)
  }

  const field = (name, label, props = {}) => (
    <TextField
      id={`customer_${name}`}
      name={`customer[${name}]`}
      label={label}
      defaultValue={values[name]}
      disabled={locked[name]}
      {...props}
    />
  )

  return (
    <OnboardingLayout step={3} rail={rail} email={email} initials={initials} signOut={signOut}>
      <Head title="Finish your profile · The Urban Writers" />

      <StepTitle kicker={stepKicker(3, rail)} title="Finish your profile">
        Your name and address go on order paperwork and receipts, so they need to match your legal details.
      </StepTitle>

      {rail === "beforePosting" && !flash.alert && (
        <AuthAlert>Add your billing address and phone to post. Save to return to the posting form.</AuthAlert>
      )}
      {flash.alert && <AuthAlert tone="bad">{flash.alert}</AuthAlert>}

      <form method="post" action={action} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <CsrfField />
        <input type="hidden" name="_method" value="put" />

        <Section first>About you</Section>
        <Grid cols={3}>
          {field("first_name", "First name", { placeholder: "Legal first name", autoComplete: "given-name", required: true, autoFocus: !locked.first_name })}
          {field("last_name", "Last name", { placeholder: "Legal last name", autoComplete: "family-name", required: true })}
          <TextField
            id="customer_username"
            name="customer[username]"
            label={<>Username <Optional /></>}
            placeholder="What freelancers call you"
            autoComplete="username"
            minLength={3}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={locked.username}
            error={availability && !availability.ok ? availability.text : undefined}
            hint={
              availability?.ok ? (
                <span className="font-semibold text-done">@{username} is available</span>
              ) : (
                USERNAME_HINT
              )
            }
          />
        </Grid>

        <Section>Address</Section>
        <Grid cols={2}>
          {field("address", "Address", { placeholder: "Street address", autoComplete: "address-line1", required: true })}
          {field("address2", <>Address 2 <Optional /></>, { placeholder: "Apt, suite, unit", autoComplete: "address-line2" })}
        </Grid>
        <Grid cols={3}>
          <CountryRegionFields
            country={country}
            region={region}
            onCountryChange={(value) => {
              setCountry(value)
              setRegion("")
              setRegionMissing(false)
            }}
            onRegionChange={(value) => {
              setRegion(value)
              setRegionMissing(false)
            }}
            saved={values}
            locked={locked}
            regionError={regionMissing ? "Choose your region." : undefined}
            copy={PICKER_COPY}
          />
          {field("city", "City", { placeholder: "City", autoComplete: "address-level2", required: true })}
        </Grid>
        <Grid cols={3}>
          {field("postal_code", "Postal code", { placeholder: "Postal / ZIP", autoComplete: "postal-code", required: true })}
          {field("phone_number", "Phone number", {
            type: "tel",
            placeholder: "+1 416 555 0199",
            autoComplete: "tel",
            required: true,
            hint: "Only used if we need to reach you about an order.",
          })}
          {field("company", <>Company <Optional /></>, { placeholder: "Company name", autoComplete: "organization" })}
        </Grid>

        <Section>Preferences</Section>
        <Grid cols={2}>
          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="user_timezone" className={LABEL_CLASS}>
              Time zone
            </Label>
            <input type="hidden" name="user[timezone]" value={timezone} />
            <Combobox
              id="user_timezone"
              options={timezoneOptions}
              value={timezone}
              onValueChange={setTimezone}
              placeholder="Select"
              searchPlaceholder="Search time zones…"
              emptyText="No time zone found."
              disabled={locked.timezone}
              className={PICKER_CLASS}
              data-testid="timezone-combobox"
            />
            <p className="text-[12px] leading-[1.45] text-ink-3">
              Detected from your browser. Change it if it’s wrong. Deadlines show in this zone.
            </p>
          </div>
        </Grid>

        <div className="flex items-center justify-between gap-3 pt-1.5 max-[560px]:flex-col-reverse max-[560px]:items-stretch">
          <span className="text-[12.5px] text-ink-3">You can update all of this later in Settings.</span>
          <Button type="submit" className="h-auto rounded-[11px] px-5 py-3 text-[14px] font-semibold">
            Save and continue
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  )
}

function Section({ first = false, children }) {
  return (
    <div
      className={cn(
        "font-display text-[13px] font-bold",
        !first && "mt-1 border-t border-border pt-2"
      )}
    >
      {children}
    </div>
  )
}

function Grid({ cols, children }) {
  return (
    <div
      className={cn(
        "grid gap-4 max-[560px]:grid-cols-1",
        cols === 3 ? "grid-cols-3 max-[900px]:grid-cols-2" : "grid-cols-2"
      )}
    >
      {children}
    </div>
  )
}
